import { buildInterviewMoment } from 'features/sendouts/common/utils';
import { formatPhoneNumber } from 'UI/utils';

/**
 * Formats interview date and time based on the selected type
 * @param {Object} interviewData - Form data
 * @returns {string} - Formatted date string
 */
export const formatInterviewDateTime = (interviewData = {}) => {
  const {
    interviewDate,
    interviewTimeFrame,
    interviewTimezone,
    interviewFromHour,
    interviewToHour
  } = interviewData;

  if (!interviewDate || !interviewTimeFrame || !interviewTimezone) {
    return '';
  }

  const timezoneName = interviewTimezone.momentName;
  const isFixedHour = interviewTimeFrame.id === 'fixedHour';

  if (isFixedHour) {
    const combinedDateTime = buildInterviewMoment({
      interviewDate,
      time: interviewFromHour,
      timezoneName
    });

    if (!combinedDateTime) return '';

    return formatFixedHour(combinedDateTime, timezoneName);
  }

  const fromDateTime = buildInterviewMoment({
    interviewDate,
    time: interviewFromHour,
    timezoneName
  });
  const toDateTime = buildInterviewMoment({
    interviewDate,
    time: interviewToHour,
    timezoneName
  });

  if (!fromDateTime || !toDateTime) return '';

  return formatTimeRange(fromDateTime, toDateTime, timezoneName);
};

const SENDOUT_TAGS = {
  hiringAuthorityFullName: '{{hiring_authority_full_name}}',
  hiringAuthorityName: '{{hiring_authority_name}}',
  hiringAuthorityEmail: '{{hiring_authority_email}}',
  jobOrderTitle: '{{job_order_title}}',
  companyName: '{{company_name}}',
  candidateName: '{{candidate_full_name}}',
  interviews: '{{interview_dates}}',
  candidatePhone: '{{candidate_phone}}',
  candidateFirstName: '{{candidate_name}}',
  candidateEmail: '{{candidate_email}}'
};

const escapeRegExp = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const escapeHtml = value =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const SENDOUT_TAG_PATTERNS = Object.keys(SENDOUT_TAGS).map(key => {
  const token = SENDOUT_TAGS[key];
  const tagName = token.replace(/{{|}}/g, '').trim();
  return {
    key,
    pattern: new RegExp(`{{\\s*${escapeRegExp(tagName)}\\s*}}`, 'g')
  };
});

export const replaceSendoutTags = (html, valuesByKey) => {
  if (!html) return '';

  let result = html;
  SENDOUT_TAG_PATTERNS.forEach(({ key, pattern }) => {
    const value = valuesByKey?.[key];
    const safeValue = value == null ? '' : escapeHtml(value);
    result = result.replace(pattern, safeValue);
  });

  return result;
};

const getResolvedTagValues = (emailBody, valuesByKey) => {
  if (!emailBody) return [];

  return SENDOUT_TAG_PATTERNS.reduce((resolvedValues, { key, pattern }) => {
    const matches = emailBody.match(pattern) || [];
    const value = valuesByKey?.[key];
    const safeValue = value == null ? '' : escapeHtml(value);

    if (!safeValue) return resolvedValues;

    const nextValues = matches.map(() => safeValue);
    return [...resolvedValues, ...nextValues];
  }, []);
};

export const buildResolvedEmailBody = ({
  emailBody = '',
  hiringAuthority = null,
  candidate = null,
  interviewData = {},
  jobOrder = {},
  candidateInfo = {}
}) => {
  const interviewDateString = formatInterviewDateTime(interviewData);
  const companyName = jobOrder?.company?.name;

  const tagValues = {
    hiringAuthorityFullName: hiringAuthority?.full_name,
    hiringAuthorityName: hiringAuthority?.first_name,
    hiringAuthorityEmail: hiringAuthority?.work_email,
    jobOrderTitle: jobOrder?.title,
    companyName,
    candidateName: candidate?.fullName,
    interviews: interviewDateString,
    candidatePhone: formatPhoneNumber(candidateInfo?.phone),
    candidateFirstName: candidateInfo?.name,
    candidateEmail: candidate?.email
  };

  return {
    resolvedEmailBody: replaceSendoutTags(emailBody, tagValues),
    resolvedTagValues: getResolvedTagValues(emailBody, tagValues)
  };
};

const getInterviewFormatters = timeZone => ({
  dateFormatter: new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone
  }),
  timeFormatter: new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone
  })
});

/**
 * Formats a date with fixed hour
 * @param {moment.Moment} dateTime - Combined date and time, zoned
 * @param {string} timezoneName - A time zone name, e.g. 'US/Central'
 * @returns {string} - Formatted string (e.g., 'Friday February 20, 06:30 PM CST')
 */
const formatFixedHour = (dateTime, timezoneName) => {
  const { dateFormatter, timeFormatter } = getInterviewFormatters(timezoneName);

  const formattedDate = dateFormatter.format(dateTime.toDate());
  const formattedTime = timeFormatter.format(dateTime.toDate());

  return `${formattedDate}, ${formattedTime} ${dateTime.format('z')}`;
};

/**
 * Formats a date with time range
 * @param {moment.Moment} fromDateTime - Start date and time, zoned
 * @param {moment.Moment} toDateTime - End date and time, zoned
 * @param {string} timezoneName - A time zone name, e.g. 'US/Central'
 * @returns {string} - Formatted string (e.g., 'Friday February 20, 06:30 PM - 09:30 PM PST')
 */
const formatTimeRange = (fromDateTime, toDateTime, timezoneName) => {
  const { dateFormatter, timeFormatter } = getInterviewFormatters(timezoneName);

  const formattedDate = dateFormatter.format(fromDateTime.toDate());
  const formattedFromTime = timeFormatter.format(fromDateTime.toDate());
  const formattedToTime = timeFormatter.format(toDateTime.toDate());

  return `${formattedDate}, ${formattedFromTime} - ${formattedToTime} ${fromDateTime.format('z')}`;
};

export default formatInterviewDateTime;
